'use strict';

define('vb/private/stateManagement/implicitInstanceFactoryVariable',['vb/private/stateManagement/instanceFactoryVariable', 'vb/private/constants'],
  (InstanceFactoryVariable, Constants) => {
    /**
     * This class represents a instance factory variable that is implicitly coerced. The implicit coercion happens when
     * a variable of type 'ojs/xyzTypeClass' does not have an explicit type definition for 'ojs/xyzTypeClass' mapping
     * it to vb/InstanceFactory type. In order to maintain backwards compatibility, the value of an implicit instance
     * factory variable will be proxied so that it behaves like an instance of 'ojs/xyzTypeClass' but also provides
     * access to the instance and constructorParams properties.
     */
    class ImplicitInstanceFactoryVariable extends InstanceFactoryVariable {
      /**
       * Wraps the instance value in a proxy so it behaves like an instance of 'ojs/xyzTypeClass' type but also
       * provides access to the instance and constructorParams properties. The instance value has the following
       * format:
       *
       * {
       *   constructorParams: [...],
       *   instance: ...
       * }
       *
       * @returns {Proxy}
       */
      getValue() {
        const value = super.getValue();

        return new Proxy(value, {
          /**
           * Returns instance or constructorParams property directly from target instance. For all other properties,
           * return it from target.instance.
           *
           * @param {Object} target
           * @param {PropertyKey} property
           * @returns {*}
           */
          get(target, property) {
            if (property === Constants.IS_IMPLICIT_INSTANCE_VARIABLE_PROXY) {
              return true;
            }
            if (property === 'instance' || property === 'constructorParams') {
              return Reflect.get(target, property);
            }
            const instanceProperty = Reflect.get(target.instance, property);
            // EventTarget methods must run on the wrapped instance so listener state is stored on the
            // same object that later dispatches events.
            if (property === 'addEventListener'
              || property === 'removeEventListener'
              || property === 'dispatchEvent') {
              return typeof instanceProperty === 'function' ? instanceProperty.bind(target.instance) : instanceProperty;
            }
            return instanceProperty;
          },

          /**
           * For instance or constructorParams, directly sets the value on the target instance. For all other
           * properties, set the value on target.instance.
           *
           * @param {Object} target
           * @param {PropertyKey} property
           * @param {*} val
           * @returns {boolean}
           */
          set(target, property, val) {
            if (property === 'instance' || property === 'constructorParams') {
              return Reflect.set(target, property, val);
            }

            return Reflect.set(target.instance, property, val);
          },

          /**
           * Returns the prototype object from target.instance. This prevents the proxy from getting cloned.
           *
           * @param {Object} target
           * @returns {Object|null}
           */
          getPrototypeOf(target) {
            return Object.getPrototypeOf(target.instance);
          },
        });
      }
    }

    return ImplicitInstanceFactoryVariable;
  });

